import {Pressable, type StyleProp, View, type ViewStyle} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {
  type MaterialPageCounts,
  type MaterialTotals,
} from '#/lib/document/material'
import {atoms as a, useTheme} from '#/alf'
import {BadgeTooltip} from '#/components/documents/BadgeTooltip'
import {Text} from '#/components/Typography'

/**
 * What a document page carries, said in a glyph and a number.
 *
 * Deliberately wordless. These sit in a footer that already reads `title · size ·
 * 3 / 28`, and the one thing a reader scans that row for is *how far in they
 * are*; spelling out "3 questions" beside every number would push the page
 * counter — the number they actually came for — off the end of the row. An icon
 * and a count say the same thing in a third of the width, and the words are still
 * there for anyone not reading with their eyes: every badge carries its meaning as
 * an accessibility label, and on the web as the element's own tooltip.
 *
 * Two weights, because there are two facts and they must not be confused:
 *
 *  - the **per-page** pair is what is on the page in front of you, and
 *  - the **totals** are what the whole document holds, on a slightly stronger
 *    surface so a glance can tell which is which without reading either.
 *
 * Both are drawn at every width. Four badges and a page counter come to well
 * under half a phone's footer, and the title beside them has `numberOfLines` for
 * the rest — where a page's own count is what a reader acts on, and the deck's is
 * what they plan against, neither is worth losing to a margin.
 *
 * A hairline stands between the two groups. Two pairs of the same glyphs in the
 * same order read as a row of four numbers otherwise — `🎧1 ❓3 🎧12 ❓40` says
 * nothing about where the page stops and the document starts — and a rule says it
 * without a word, at the same size as the badges it separates.
 *
 * Zero is not drawn. A page with no material, and a document with none either,
 * both render nothing at all — an empty pill would be furniture announcing the
 * absence of furniture.
 *
 * The page's question badge is also the door to its questions, when the surface
 * it is drawn on has somewhere to open: `onPressPage`. A count that a reader can
 * press is a count that tells them where the questions are.
 *
 * Built from `View` and `Text` rather than from any one surface's chrome, because
 * the same badges are drawn in the web carousel's footer and on the native
 * document card, and the two must agree about what a document holds.
 */

/** Audio wears the glyph the author recorded it with, wherever it is drawn. */
const AUDIO_GLYPH = 'volume-2' as const
const QUESTIONS_GLYPH = 'help-circle' as const

export function DocumentMaterialBadges({
  page,
  totals,
  onPressPage,
  style,
}: {
  /** This page's counts. Omitted when the surface has no current page. */
  page?: MaterialPageCounts
  /** The whole document's counts. Omitted when only the page is known. */
  totals?: MaterialTotals
  /**
   * What pressing this page's question badge does.
   *
   * The badge is a count and also a door: it is how a reader gets back to the
   * questions of a page they closed, and the only way into the questions of the
   * first page, which no reader has turned away from yet. Omitted where there is
   * nothing to open, so a count that is only a count is not a button.
   */
  onPressPage?: () => void
  style?: StyleProp<ViewStyle>
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const showPage = !!page && (page.audio > 0 || page.questions > 0)
  const showTotals = !!totals && (totals.audio > 0 || totals.questions > 0)

  if (!showPage && !showTotals) return null

  return (
    <View
      style={[a.flex_row, a.align_center, a.gap_xs, a.flex_shrink_0, style]}>
      {showPage && page && (
        <>
          {page.audio > 0 && (
            <Badge
              glyph={AUDIO_GLYPH}
              count={page.audio}
              label={plural(page.audio, {
                one: '# audio clip on this page',
                other: '# audio clips on this page',
              })}
            />
          )}
          {page.questions > 0 && (
            <Badge
              glyph={QUESTIONS_GLYPH}
              count={page.questions}
              onPress={onPressPage}
              label={plural(page.questions, {
                one: '# question on this page',
                other: '# questions on this page',
              })}
              hint={l`Opens this page's questions`}
            />
          )}
        </>
      )}
      {showTotals && totals && (
        <View style={[a.flex_row, a.align_center, a.gap_xs]}>
          {showPage && (
            <View
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              style={[
                a.self_center,
                {
                  width: 1,
                  height: 12,
                  backgroundColor: t.palette.contrast_200,
                },
              ]}
            />
          )}
          {totals.audio > 0 && (
            <Badge
              glyph={AUDIO_GLYPH}
              count={totals.audio}
              emphasis
              label={plural(totals.audio, {
                one: '# audio clip in this document',
                other: '# audio clips in this document',
              })}
            />
          )}
          {totals.questions > 0 && (
            <Badge
              glyph={QUESTIONS_GLYPH}
              count={totals.questions}
              emphasis
              label={plural(totals.questions, {
                one: '# question in this document',
                other: '# questions in this document',
              })}
            />
          )}
        </View>
      )}
    </View>
  )
}

function Badge({
  glyph,
  count,
  label,
  emphasis = false,
  onPress,
  hint,
}: {
  glyph: typeof AUDIO_GLYPH | typeof QUESTIONS_GLYPH
  count: number
  /** The words, for anyone not reading the number visually. */
  label: string
  /** A deck total rather than a page's own count — see the module note. */
  emphasis?: boolean
  /** Present when this badge opens something; absent when it only counts. */
  onPress?: () => void
  /** What pressing it does. Only read when there is something to press. */
  hint?: string
}) {
  const t = useTheme()
  /*
   * The label is already translated: every caller builds it with `plural(...)`,
   * which is the macro that reads the catalogue. Running `_()` over its result
   * would be asking the catalogue for a message keyed by the sentence it just
   * returned, and would answer with the key it could not find.
   *
   * The same sentence is both the accessibility label and the tooltip, so a badge
   * says one thing about itself however it is asked.
   */
  const body = (
    <>
      <Feather
        name={glyph}
        size={11}
        color={
          emphasis ? t.atoms.text.color : t.atoms.text_contrast_medium.color
        }
      />
      <Text
        style={[
          a.text_2xs,
          a.font_bold,
          emphasis ? t.atoms.text : t.atoms.text_contrast_medium,
        ]}>
        {count}
      </Text>
    </>
  )
  const surface: StyleProp<ViewStyle> = [
    a.flex_row,
    a.align_center,
    a.gap_2xs,
    a.px_xs,
    a.py_2xs,
    a.rounded_xs,
    emphasis ? t.atoms.bg_contrast_100 : t.atoms.bg_contrast_50,
  ]
  if (onPress) {
    return (
      <BadgeTooltip label={label}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={label}
          accessibilityHint={hint ?? ''}
          onPress={onPress}
          style={({pressed}) => [...surface, {opacity: pressed ? 0.7 : 1}]}>
          {body}
        </Pressable>
      </BadgeTooltip>
    )
  }
  return (
    <BadgeTooltip label={label}>
      <View
        accessibilityRole="text"
        accessibilityLabel={label}
        accessibilityHint=""
        style={surface}>
        {body}
      </View>
    </BadgeTooltip>
  )
}
